import {
  colors,
  muiChipStyleOverridesLabelSmall,
  muiChipStyleOverridesSizeSmall,
} from '@globals/themes/defaultTheme'
import { desktopQuery, tabletQuery } from '@globals/themes/themeConstants'
import { MediaObjectUrl } from '@kudos/http-client'
import MoreActionsMenu from '@layouts/standardPageLayout/MoreActionsMenu'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { SxProps } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import UserAvatar from '@shared/avatar/UserAvatar'
import { ReactNode, useLayoutEffect, useRef, useState } from 'react'
import { ActionButton } from './button/types'

const { ['.MuiChip-label']: chipSizeSmallLabelNested, ...chipSizeSmallRootStyles } =
  muiChipStyleOverridesSizeSmall

const cardStyle: SxProps = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  [desktopQuery]: {
    borderRight: `1px solid ${colors.grayExtraLight}`,
    borderBottom: `1px solid ${colors.grayExtraLight}`,
    borderLeft: `1px solid ${colors.grayExtraLight}`,
  },
}

const headerStyle: SxProps = {
  width: '100%',
  borderBottom: `1px solid ${colors.grayExtraLight}`,
  alignItems: 'center',
}

const titleChipStyle: SxProps = {
  width: '25%',
  flex: '1 1 33%',
  verticalAlign: 'middle',
  alignItems: 'center',
  [tabletQuery]: {
    gap: 0.5,
    '& .MuiChip-root': {
      margin: 0,
      ...chipSizeSmallRootStyles,
      '& .MuiChip-label': {
        ...chipSizeSmallLabelNested,
        ...muiChipStyleOverridesLabelSmall,
      },
    },
  },
}

const titleStyle: SxProps = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: '1 2 auto',
  pr: 1,
  mt: 0,
  [tabletQuery]: {
    mt: 0,
  },
}

type StandardCardProps = {
  title: ReactNode
  subtitle?: ReactNode
  attachmentUrls?: MediaObjectUrl | null
  defaultIcon?: ReactNode
  chip?: ReactNode
  children?: ReactNode
  borderColor?: string
  bottomLeftActions?: ReactNode
  bottomRightActions?: ReactNode
  actionButton?: ReactNode
  onClick?: () => void
  actions?: ActionButton[] | ReactNode
  disabled?: boolean
  removeBorder?: boolean
  maxContentLines?: number
}

const StandardCard: React.FC<StandardCardProps> = ({
  title,
  subtitle,
  attachmentUrls,
  defaultIcon,
  chip,
  children,
  borderColor,
  bottomLeftActions,
  bottomRightActions,
  actionButton,
  onClick,
  actions,
  disabled = false,
  removeBorder = false,
  maxContentLines,
}) => {
  const isTablet = useMediaQuery(tabletQuery)
  const childrenRef = useRef<HTMLDivElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const shouldClamp = !!maxContentLines && !isTablet

  useLayoutEffect(() => {
    const el = childrenRef.current
    if (!el || !shouldClamp) {
      setIsOverflowing(false)
      return
    }
    const check = () => setIsOverflowing(el.scrollHeight > el.clientHeight)
    check()
    const observer = new ResizeObserver(check)
    observer.observe(el)
    return () => observer.disconnect()
  }, [shouldClamp])

  const borderTop = (isTablet: boolean) => {
    if (disabled) return `6px solid ${colors.grayExtraLight}`
    if (borderColor) return `6px solid ${borderColor}`
    return isTablet ? `1px solid ${colors.grayUltraLight}` : `1px solid ${colors.grayExtraLight}`
  }

  return (
    <Card
      sx={{
        ...cardStyle,
        borderTop: `${borderTop(isTablet)} !important`,
        cursor: !!onClick ? 'pointer' : 'auto',
        filter: disabled ? 'grayscale(1)' : 'grayscale(0)',
      }}
      onClick={onClick}>
      <Stack
        direction="row"
        spacing={1.5}
        p={2}
        pr={!!actions ? 1 : 2}
        pt={title || subtitle ? 2 : 0}
        sx={removeBorder ? { ...headerStyle, borderBottom: 'none', pb: 0 } : headerStyle}>
        {(!!attachmentUrls || !!defaultIcon) && (
          <UserAvatar media={attachmentUrls} size={58} defaultIcon={defaultIcon} />
        )}
        <Stack direction="row" sx={titleChipStyle}>
          <Stack sx={titleStyle}>
            <Typography variant="h3" component="h2" sx={titleStyle}>
              {title}
            </Typography>
            <Typography variant="caption" sx={titleStyle}>
              {subtitle}
            </Typography>
            {!!chip && isTablet && chip}
          </Stack>
          {!!chip && !isTablet && chip}
          {!!actions && (Array.isArray(actions) ? <MoreActionsMenu actions={actions} /> : actions)}
        </Stack>
      </Stack>
      {!!children && (
        <Tooltip
          title={isOverflowing ? <Box sx={{ '& *': { color: 'inherit' } }}>{children}</Box> : ''}
          placement="bottom-start"
          disableHoverListener={!isOverflowing}
          slotProps={{ tooltip: { sx: { maxWidth: 384 } } }}>
          <Box sx={{ p: 2, height: '100%' }}>
            <Box
              ref={childrenRef}
              sx={{
                height: '100%',
                ...(shouldClamp && {
                  display: '-webkit-box',
                  WebkitLineClamp: maxContentLines,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }),
              }}>
              {children}
            </Box>
          </Box>
        </Tooltip>
      )}
      {(bottomLeftActions || bottomRightActions || (!isTablet && actionButton)) && (
        <Stack direction="row" px={2} pb={2} alignItems="flex-end" gap={1}>
          <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>{bottomLeftActions}</Box>
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            {bottomRightActions}
            {!isTablet && actionButton}
          </Stack>
        </Stack>
      )}
      {isTablet && !!actionButton && (
        <Stack px={2} pb={2}>
          {actionButton}
        </Stack>
      )}
    </Card>
  )
}

export default StandardCard
